// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { useCallback, useMemo } from 'react';
import {
  type ExpandedState,
  type SortingFn,
  createColumnHelper,
} from '@tanstack/react-table';
import { AssetBrand, IconButton, Table } from '@shared/ui-kit/components';
import { selectAccountCurrency } from '@shared/data-access/slices/user';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { type AccountsHoldingsData } from '@shared/data-access/interfaces/api/brokerage/brokerage';
import { getAssetBrandLogoUrl } from '@shared/data-access/utils';
import {
  createProductFamilyMap,
  createMarketClassMap,
  getPositionsGroupRowLabel,
  getProductFamilyOrder,
} from '@shared/advisory/aggregation/utils/product-family-labels';
import { Tooltip } from '@mui/material';

export interface UsePositionsTableProps {
  readonly data: AccountsHoldingsData[];
  readonly initialExpanded?: ExpandedState;
  readonly onDownload: (row: AccountsHoldingsData) => void;
  readonly isMobile: boolean;
  readonly viewMode: 'market' | 'product-family';
}

const columnHelper = createColumnHelper<AccountsHoldingsData>();

const MARKET_ORDER = [
  'Money Market',
  'Fixed Income',
  'Equity',
  'Alternative Investments',
  'Other',
] as const;

/** Gets the order index for a market (asset_class) for sorting purposes */
const getMarketOrder = (assetClass: string): number => {
  const index = MARKET_ORDER.findIndex(
    market => market.toLowerCase() === assetClass.toLowerCase(),
  );

  return index >= 0 ? index : MARKET_ORDER.length;
};

export const usePositionsTable = ({
  initialExpanded = true,
  onDownload,
  isMobile,
  viewMode,
}: UsePositionsTableProps) => {
  const { t } = useTranslation();
  const selectedCurrency = useSelector(selectAccountCurrency);

  const productFamilyMap = useMemo(() => createProductFamilyMap(t), [t]);
  const marketClassMap = useMemo(() => createMarketClassMap(t), [t]);

  const sortColumnName: SortingFn<AccountsHoldingsData> = useCallback(
    (x, y, columnId) => {
      const xValue = x.getValue(columnId);
      const yValue = y.getValue(columnId);

      /* istanbul ignore next */
      // Total always goes to the top
      if (xValue === 'total' || yValue === 'total') {
        return xValue === 'total' ? -1 : 1;
      }

      // "other" always goes to the end
      const xIsOther =
        xValue === 'other' || xValue?.toString().toLowerCase() === 'other';
      const yIsOther =
        yValue === 'other' || yValue?.toString().toLowerCase() === 'other';

      if (xIsOther && !yIsOther) return 1;
      if (!xIsOther && yIsOther) return -1;

      // Sort by market order (asset_class) or product family when grouped by type
      const xGroupKey = x.original.asset_class ?? '';
      const yGroupKey = y.original.asset_class ?? '';

      if (viewMode === 'product-family') {
        const orderX = getProductFamilyOrder(xGroupKey);
        const orderY = getProductFamilyOrder(yGroupKey);

        if (orderX !== orderY) {
          return orderX - orderY;
        }
      } else {
        const orderX = getMarketOrder(xGroupKey);
        const orderY = getMarketOrder(yGroupKey);

        if (orderX !== orderY) {
          return orderX - orderY;
        }
      }

      // Same market: alphabetical comparison
      const xStr = xValue?.toString() || '';
      const yStr = yValue?.toString() || '';

      return xStr.localeCompare(yStr);
    },
    [viewMode],
  );

  const columns = useMemo(
    () => [
      columnHelper.accessor('name', {
        header: t('common.name', { ns: 'common' }),
        enableSorting: true,
        sortingFn: sortColumnName,
        id: 'name',
        cell: ({ getValue, row }) => {
          return (
            <div className="flex items-center gap-x-2 truncate">
              <Table.Expander expander={row} />
              {row.getCanExpand() || getValue() === 'total' ? (
                <span className="uppercase">
                  {getPositionsGroupRowLabel({
                    name: getValue(),
                    viewMode,
                    productFamilyMap,
                    marketClassMap,
                    t,
                  })}
                </span>
              ) : (
                <AssetBrand
                  ticker={row.original.ticker ?? row.original.isin!}
                  assetType={
                    row.original.details?.asset_type ?? row.original.asset_type
                  }
                  logoUrl={getAssetBrandLogoUrl(row.original.details)}
                  url={row.original.details?.url_morningstar}
                  label={row.original.name}
                  isMobile={isMobile}
                />
              )}
            </div>
          );
        },
        maxSize: isMobile ? 220 : 350,
      }),

      columnHelper.accessor(
        row => (viewMode === 'market' ? row.asset_type : row.asset_class),
        {
          header:
            viewMode === 'market'
              ? t('glossary.product-family', { ns: 'glossary' })
              : t('glossary.market', { ns: 'glossary' }),
          id: viewMode === 'market' ? 'product_family' : 'market',
          cell: ({ row }) =>
            row.original.name === 'total' || row.getCanExpand()
              ? ''
              : viewMode === 'market'
              ? productFamilyMap.get(row.original.asset_type) ||
                row.original.asset_type
              : row.original.asset_class,
          maxSize: isMobile ? 120 : 150,
        },
      ),

      // columnHelper.accessor('account', {
      //   header: t('common.account', { ns: 'common' }),
      //   id: 'account',
      //   cell: ({ row }) => (
      //     <div className="flex flex-col">
      //       <span className="text-sm">{row.original.institution_name}</span>
      //       <span className="text-sm">{row.original.iban}</span>
      //     </div>
      //   ),
      // }),

      columnHelper.accessor(row => row.ticker ?? row.isin, {
        header: t('common.ticker-isin', { ns: 'common' }),
        id: 'ticker-isin',
        cell: ({ getValue }) => getValue() ?? '',
      }),

      columnHelper.accessor('currency', {
        header: t('common.currency', { ns: 'common' }),
        id: 'currency',
        cell: /* istanbul ignore next */ ({ getValue }) => getValue() ?? 'USD',
        maxSize: isMobile ? 110 : undefined,
      }),

      columnHelper.accessor('weight', {
        header: t('common.weight', { ns: 'common' }),
        id: 'weight',
        meta: { initiallyHidden: isMobile, alignment: 'end' },
        cell: ({ getValue }) => (
          <Table.Data value={getValue()} format="percentage" />
        ),
      }),

      columnHelper.accessor('quantity', {
        header: t('common.quantity', { ns: 'common' }),
        id: 'quantity',
        meta: { initiallyHidden: true, alignment: 'end' },
        cell: ({ getValue }) => (
          <Table.Data value={getValue()} format="decimal" emptyValue={-1} />
        ),
      }),

      columnHelper.accessor('investment', {
        header: t('common.investment-currency', {
          ns: 'common',
          currency: selectedCurrency,
        }),
        id: 'investment',
        meta: {
          alignment: 'end',
        },
        cell: ({ getValue }) => (
          <Table.Data
            value={getValue()}
            format="currency"
            currency={selectedCurrency}
          />
        ),
      }),

      columnHelper.accessor(row => row.details?.ytd_return, {
        id: 'ytd_return',
        header: t('glossary.investment-returns-ytd', { ns: 'glossary' }),
        meta: { alignment: 'end' },
        cell: ({ getValue }) => (
          <Table.Data value={getValue()} format="percentage" validate />
        ),
      }),

      columnHelper.accessor(row => row.details?.y1_return, {
        id: 'y1_return',
        header: t('glossary.investment-returns-1y', { ns: 'glossary' }),
        meta: { initiallyHidden: true, alignment: 'end' },
        cell: ({ getValue }) => (
          <Table.Data value={getValue()} format="percentage" validate />
        ),
      }),

      columnHelper.accessor(row => row.details?.inception_return, {
        id: 'inception_return',
        header: t('glossary.investment-returns-inception', { ns: 'glossary' }),
        meta: {
          initiallyHidden: true,
          alignment: 'end',
        },
        cell: ({ getValue }) => (
          <Table.Data value={getValue()} format="percentage" validate />
        ),
      }),

      columnHelper.accessor(row => row.details?.m1_return, {
        header: t('glossary.investment-returns-m1', { ns: 'glossary' }),
        id: 'm1_return',
        meta: {
          initiallyHidden: true,
          alignment: 'end',
        },
        cell: ({ getValue }) => (
          <Table.Data value={getValue()} format="percentage" validate />
        ),
      }),

      columnHelper.accessor(row => row.details?.m3_return, {
        header: t('glossary.investment-returns-m3', { ns: 'glossary' }),
        id: 'm3_return',
        meta: {
          initiallyHidden: true,
          alignment: 'end',
        },
        cell: ({ getValue }) => (
          <Table.Data value={getValue()} format="percentage" validate />
        ),
      }),

      columnHelper.accessor(row => row.details?.m6_return, {
        header: t('glossary.investment-returns-m6', { ns: 'glossary' }),
        id: 'm6_return',
        meta: {
          initiallyHidden: true,
          alignment: 'end',
        },
        cell: ({ getValue }) => (
          <Table.Data value={getValue()} format="percentage" validate />
        ),
      }),

      columnHelper.display({
        id: 'options',
        enableSorting: false,
        meta: { showColumnVisibilitySelector: true },
        cell: ({ row, table }) => {
          if (!row.getCanExpand() && row.original.name !== 'total') return null;

          const data =
            row.original.name === 'total'
              ? ({
                  name: 'total',
                  subRows: table
                    .getCoreRowModel()
                    .rows.flatMap(row => row.original.subRows),
                } as AccountsHoldingsData)
              : row.original;

          return (
            <div className="flex w-full justify-end gap-x-1">
              <Tooltip title={t('common.download', { ns: 'common' })}>
                <IconButton
                  icon="Download"
                  variant="icon"
                  size="lg"
                  onClick={() => onDownload(data)}
                />
              </Tooltip>
            </div>
          );
        },
      }),
    ],
    [
      t,
      sortColumnName,
      isMobile,
      viewMode,
      selectedCurrency,
      productFamilyMap,
      marketClassMap,
      onDownload,
    ],
  );

  return {
    columns,
    initialExpanded,
  };
};
